/**
 * SharePoint Utilities
 * Contains reusable helper functions for SharePoint operations.
 */

// Regular expression for validating GUIDs
export const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Creates a list endpoint fragment (getByTitle or getById) based on the list identifier.
 * 
 * @param {string} listIdentifier - The list title or GUID
 * @returns {string} The endpoint fragment to use in SharePoint REST API calls
 */
export function getListEndpointFragment(listIdentifier) {
  if (GUID_REGEX.test(listIdentifier)) {
    return `getById('${listIdentifier}')`;
  } else {
    return `getByTitle('${listIdentifier}')`;
  }
}

/**
 * Parses a SharePoint error response to extract the most useful error message.
 * 
 * @param {Object|string} response - The error response from SharePoint
 * @param {number} [statusCode] - The HTTP status code if available
 * @returns {string} The extracted error message
 */
export function parseSpErrorMessage(response, statusCode) {
  if (typeof response === 'string') {
    return response;
  }
  
  const message = response?.['odata.error']?.message?.value || 
                  response?.error?.message?.value || 
                  response?.message || 
                  'Unknown SharePoint error';
  
  if (statusCode) {
    return `${message} (Status: ${statusCode})`;
  }
  
  return message;
}

/**
 * Checks if an error appears to be a digest token expiration error.
 * 
 * @param {Object|Error} error - The error to check
 * @param {number} [statusCode] - The HTTP status code if available
 * @returns {boolean} True if the error appears to be a digest expiration
 */
export function isDigestExpirationError(error, statusCode) {
  const message = typeof error === 'string' ? error : 
                 (error?.message || error?.['odata.error']?.message?.value || '');
  
  return (statusCode === 403 || error?.status === 403) && 
         (message.includes('security validation') || message.includes('request digest'));
}

/**
 * Detects if an error indicates a schema mismatch between client and SharePoint.
 * 
 * @param {Object|Error} error - The error to check
 * @returns {boolean} True if the error suggests a schema mismatch
 */
export function isSchemaMismatchError(error) {
  const message = (typeof error === 'string' ? error : 
                  (error?.message || error?.['odata.error']?.message?.value || '')).toLowerCase();
  
  return (error?.status === 400 && 
         (message.includes('field') || message.includes('column')) && 
         (message.includes('does not exist') || message.includes('not found'))) ||
         message.includes('listitementitytypefullname');
}

export function isLocalhost(){
  return typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.startsWith('localhost:'));
}